import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  Database,
  Inbox,
  MessageSquareText,
  RefreshCw,
  Server,
  Webhook,
  Headphones,
} from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/skeleton'
import { api } from '../../lib/api'
import { relativeTime } from '../../lib/format'
import { useToast } from '../../components/ui/toast'

export function SystemPage() {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: api.systemStatus,
    refetchInterval: 30_000,
  })
  const errors = useQuery({
    queryKey: ['system-errors'],
    queryFn: api.systemErrors,
    refetchInterval: 30_000,
  })
  const freeScoutJobs = useQuery({
    queryKey: ['freescout-failed-jobs'],
    queryFn: api.freeScoutFailedJobs,
    refetchInterval: 30_000,
  })
  const retryFreeScout = useMutation({
    mutationFn: api.retryFreeScoutJob,
    onSuccess: () => {
      showToast('Messaggio FreeScout rimesso in coda')
      void queryClient.invalidateQueries({ queryKey: ['freescout-failed-jobs'] })
      void queryClient.invalidateQueries({ queryKey: ['system-status'] })
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Riprova fallita', 'error'),
  })

  const counts = status.data?.database.counts
  const cards = [
    { label: 'Conversazioni', value: counts?.conversations ?? 0, icon: Inbox },
    { label: 'Messaggi', value: counts?.messages ?? 0, icon: MessageSquareText },
    { label: 'Non letti', value: counts?.unread ?? 0, icon: Activity },
    {
      label: 'Errori ultimi 7 giorni',
      value: counts?.recentErrors ?? 0,
      icon: AlertTriangle,
    },
  ]

  return (
    <section className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-10">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--primary)]">
            Diagnostica
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">Stato sistema</h1>
          <p className="mt-2 text-base leading-7 text-[var(--muted-foreground)]">
            Una vista essenziale su applicazione, database e canale Dualhook.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          disabled={status.isFetching}
          onClick={() => void status.refetch()}
        >
          <RefreshCw className={`size-4 ${status.isFetching ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Aggiorna</span>
        </Button>
      </header>

      {status.isLoading ? (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-32" />
          ))}
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((item) => (
              <Card key={item.label} className="p-5">
                <div className="flex items-center justify-between">
                  <div className="grid size-10 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                    <item.icon className="size-5" />
                  </div>
                  <span className="text-3xl font-semibold tracking-tight">
                    {item.value.toLocaleString('it-IT')}
                  </span>
                </div>
                <p className="mt-4 text-sm font-medium text-[var(--muted-foreground)]">
                  {item.label}
                </p>
              </Card>
            ))}
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-3">
            <Card className="p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-[var(--muted)]">
                  <Server className="size-5" />
                </div>
                <div>
                  <h2 className="font-semibold">Applicazione</h2>
                  <p className="text-xs text-[var(--muted-foreground)]">Runtime PEMMY2</p>
                </div>
                <Badge className="ml-auto bg-[var(--success-soft)] text-[var(--success)]">
                  Operativa
                </Badge>
              </div>
              <dl className="mt-6 grid grid-cols-2 gap-5 text-sm">
                <div>
                  <dt className="text-[var(--muted-foreground)]">Versione</dt>
                  <dd className="mt-1 font-semibold">v{status.data?.application.version}</dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Ambiente</dt>
                  <dd className="mt-1 font-semibold capitalize">
                    {status.data?.application.environment}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Database</dt>
                  <dd className="mt-1 flex items-center gap-1.5 font-semibold">
                    <Database className="size-4" /> SQLite
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Uptime</dt>
                  <dd className="mt-1 font-semibold">
                    {Math.floor((status.data?.application.startedAt ?? 0) / 60)} min
                  </dd>
                </div>
              </dl>
            </Card>

            <Card className="p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                  <Webhook className="size-5" />
                </div>
                <div>
                  <h2 className="font-semibold">Dualhook</h2>
                  <p className="text-xs text-[var(--muted-foreground)]">API e ricezione webhook</p>
                </div>
                <Badge
                  className={`ml-auto ${status.data?.dualhook.connection.connected ? 'bg-[var(--success-soft)] text-[var(--success)]' : 'bg-[var(--warning-soft)] text-[var(--warning)]'}`}
                >
                  {status.data?.dualhook.connection.connected ? 'Connesso' : 'Da configurare'}
                </Badge>
              </div>
              <dl className="mt-6 grid gap-5 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-[var(--muted-foreground)]">Ultimo webhook</dt>
                  <dd className="mt-1 font-semibold">
                    {relativeTime(status.data?.dualhook.lastWebhookAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Ultimo controllo API</dt>
                  <dd className="mt-1 font-semibold">
                    {relativeTime(status.data?.dualhook.connection.checkedAt)}
                  </dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Outbox</dt>
                  <dd className="mt-1 font-semibold">{counts?.pendingOutbox ?? 0} in coda</dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Webhook inbox</dt>
                  <dd className="mt-1 font-semibold">{counts?.pendingWebhooks ?? 0} in coda</dd>
                </div>
              </dl>
            </Card>
            <Card className="p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="grid size-10 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                  <Headphones className="size-5" />
                </div>
                <div>
                  <h2 className="font-semibold">FreeScout</h2>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    Ticket WhatsApp non gestiti
                  </p>
                </div>
                <Badge
                  className={`ml-auto ${status.data?.freeScout.connection.connected ? 'bg-[var(--success-soft)] text-[var(--success)]' : 'bg-[var(--warning-soft)] text-[var(--warning)]'}`}
                >
                  {status.data?.freeScout.connection.connected ? 'Connesso' : 'Da configurare'}
                </Badge>
              </div>
              <dl className="mt-6 grid grid-cols-2 gap-5 text-sm">
                <div>
                  <dt className="text-[var(--muted-foreground)]">In coda</dt>
                  <dd className="mt-1 font-semibold">{status.data?.freeScout.queue.queued ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">In retry</dt>
                  <dd className="mt-1 font-semibold">{status.data?.freeScout.queue.retry ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Falliti</dt>
                  <dd className="mt-1 font-semibold">{status.data?.freeScout.queue.failed ?? 0}</dd>
                </div>
                <div>
                  <dt className="text-[var(--muted-foreground)]">Ultimo test</dt>
                  <dd className="mt-1 font-semibold">
                    {relativeTime(status.data?.freeScout.connection.checkedAt)}
                  </dd>
                </div>
              </dl>
            </Card>
          </div>

          {freeScoutJobs.data?.items.length ? (
            <Card className="mt-5 p-5 sm:p-6">
              <h2 className="font-semibold">Inoltri FreeScout da ripristinare</h2>
              <ul className="mt-4 grid gap-3">
                {freeScoutJobs.data.items.map((job) => (
                  <li
                    key={job.id}
                    className="flex flex-col gap-3 rounded-xl border border-[var(--destructive-border)] bg-[var(--destructive-soft)] p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="text-sm font-semibold text-[var(--destructive)]">
                        {job.errorMessage ?? 'Errore FreeScout'}
                      </p>
                      <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                        {job.attempts} tentativi · {relativeTime(job.createdAt)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={retryFreeScout.isPending}
                      onClick={() => retryFreeScout.mutate(job.id)}
                    >
                      Rimetti in coda
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}

          <Card className="mt-5 p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="grid size-10 place-items-center rounded-xl bg-[var(--destructive-soft)] text-[var(--destructive)]">
                <AlertTriangle className="size-5" />
              </div>
              <div>
                <h2 className="font-semibold">Errori WhatsApp recenti</h2>
                <p className="text-xs text-[var(--muted-foreground)]">
                  Eventi non associabili a uno specifico messaggio della chat
                </p>
              </div>
            </div>
            {errors.isLoading ? (
              <div className="mt-5 grid gap-3">
                {Array.from({ length: 3 }, (_, index) => (
                  <Skeleton key={index} className="h-20" />
                ))}
              </div>
            ) : errors.data?.items.length ? (
              <ul className="mt-5 grid gap-3" aria-label="Errori WhatsApp recenti">
                {errors.data.items.map((event) => (
                  <li
                    key={event.id}
                    className="rounded-xl border border-[var(--destructive-border)] bg-[var(--destructive-soft)] p-4"
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <p className="text-sm font-semibold leading-6 text-[var(--destructive)]">
                        {event.message}
                      </p>
                      <time className="shrink-0 text-xs text-[var(--muted-foreground)]">
                        {relativeTime(event.createdAt)}
                      </time>
                    </div>
                    {event.details && (
                      <p className="mt-1 break-words text-sm leading-6 text-[var(--muted-foreground)]">
                        {event.details}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-5 rounded-xl bg-[var(--muted)] p-4 text-sm text-[var(--muted-foreground)]">
                Nessun errore WhatsApp generale registrato.
              </p>
            )}
            {errors.isError && (
              <p className="mt-5 text-sm text-[var(--destructive)]">
                Impossibile caricare il registro errori.
              </p>
            )}
          </Card>
        </>
      )}

      {status.isError && (
        <Card className="mt-8 border-[var(--destructive-border)] bg-[var(--destructive-soft)] p-5 text-[var(--destructive)]">
          Impossibile leggere lo stato del sistema.
        </Card>
      )}
    </section>
  )
}
